import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Handshake, Building, Users, TrendingUp, Clock, HeartHandshake, ArrowRight } from 'lucide-react';
import { useForceAnimation } from '../hooks/useForceAnimation';

const SpecialtyStructures = () => {
  const [ref, inView] = useInView({
    triggerOnce: false,
    threshold: 0,
    rootMargin: '0px 0px 30% 0px',
  });

  const isVisible = useForceAnimation(inView);

  const dealStructures = [
    {
      icon: <Handshake size={28} />,
      title: 'Founder Transition Acquisitions',
      description: 'Acquisition conversations for founders who want a thoughtful handoff for a software or online business they have built.',
      color: 'from-blue-500 to-blue-600',
      lightColor: 'from-blue-50 to-blue-100',
      iconColor: 'text-blue-600',
      gradient: 'bg-gradient-to-br from-blue-500/80 to-blue-600',
    },
    {
      icon: <Building size={28} />,
      title: 'AI SaaS Acquisitions',
      description: 'Targeted interest in AI-related SaaS products with clear customer value, usable data, and room to scale.',
      color: 'from-purple-500 to-purple-600',
      lightColor: 'from-purple-50 to-purple-100',
      iconColor: 'text-purple-600',
      gradient: 'bg-gradient-to-br from-purple-500/80 to-purple-600',
    },
    {
      icon: <Users size={28} />,
      title: 'Workflow Software',
      description: 'We are interested in software businesses that solve recurring operational problems for teams, operators, and digital businesses.',
      color: 'from-green-500 to-green-600',
      lightColor: 'from-green-50 to-green-100',
      iconColor: 'text-green-600',
      gradient: 'bg-gradient-to-br from-green-500/80 to-green-600',
    },
    {
      icon: <TrendingUp size={28} />,
      title: 'Digital Product Roll-Ups',
      description: 'Opportunities to acquire and strengthen complementary digital products, tools, and internet-native businesses.',
      color: 'from-red-500 to-red-600',
      lightColor: 'from-red-50 to-red-100',
      iconColor: 'text-red-600',
      gradient: 'bg-gradient-to-br from-red-500/80 to-red-600',
    },
    {
      icon: <Clock size={28} />,
      title: 'Phased Founder Exits',
      description: 'Structured transition paths for founders who want continuity while stepping back over time.',
      color: 'from-teal-500 to-teal-600',
      lightColor: 'from-teal-50 to-teal-100',
      iconColor: 'text-teal-600',
      gradient: 'bg-gradient-to-br from-teal-500/80 to-teal-600',
    },
    {
      icon: <HeartHandshake size={28} />,
      title: 'Strategic Combinations',
      description: 'Selective combinations of adjacent software or online businesses when the product fit and operating logic are clear.',
      color: 'from-amber-500 to-amber-600',
      lightColor: 'from-amber-50 to-amber-100',
      iconColor: 'text-amber-600',
      gradient: 'bg-gradient-to-br from-amber-500/80 to-amber-600',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  const CardWrapper = ({ children }: { children: React.ReactNode }) => {
    return <div className="relative h-full rounded-2xl p-px overflow-hidden">{children}</div>;
  };

  return (
    <section id="specialty-structures" className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -right-40 -top-40 w-80 h-80 bg-primary-100 rounded-full filter blur-3xl opacity-30"></div>
        <div className="absolute -left-20 bottom-0 w-60 h-60 bg-secondary-100 rounded-full filter blur-3xl opacity-20"></div>
        <div className="absolute right-1/4 bottom-1/4 w-40 h-40 bg-accent-100 rounded-full filter blur-3xl opacity-20"></div>
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.4 }}
            className="inline-flex items-center py-1.5 px-4 rounded-full bg-gradient-to-r from-primary-100 to-primary-50 text-primary-600 text-sm font-medium mb-4 shadow-sm"
          >
            <motion.span
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
              className="mr-2"
            >
              <Handshake size={16} />
            </motion.span>
            Acquisition Paths
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="text-3xl md:text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700"
          >
            Where We Like to Spend Time
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="text-lg text-gray-600 leading-relaxed"
          >
            We focus on acquisitions and venture opportunities tied to AI-related SaaS startups and online businesses.
            The emphasis is on digital products, practical founder transitions, and businesses that can be improved through
            better product and operating execution.
          </motion.p>
        </div>

        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? 'visible' : 'hidden'}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {dealStructures.map((structure, index) => (
            <CardWrapper key={index}>
              <motion.div
                variants={itemVariants}
                whileHover={{
                  y: -5,
                  transition: { duration: 0.3 },
                }}
                className="relative h-full bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden group"
              >
                <div className={`absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-500 bg-gradient-to-br ${structure.color}`}></div>

                <div className="relative">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${structure.lightColor} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <div className={structure.iconColor}>{structure.icon}</div>
                  </div>

                  <h3 className="text-xl font-bold mb-4 group-hover:text-primary-600 transition-colors duration-300">{structure.title}</h3>
                  <p className="text-gray-600 mb-6">{structure.description}</p>

                  <div className="flex items-center text-primary-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="mr-2">Learn more</span>
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </div>

                <div className={`absolute -bottom-2 -right-2 w-16 h-16 rounded-full ${structure.gradient} opacity-20 group-hover:opacity-40 transition-opacity duration-300`}></div>
              </motion.div>
            </CardWrapper>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          className="mt-20 text-center"
        >
          <motion.a
            whileHover={{ scale: 1.05, boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.4)' }}
            whileTap={{ scale: 0.95 }}
            href="#contact"
            className="inline-flex items-center px-6 py-3 rounded-lg bg-gradient-to-r from-primary-600 to-primary-500 text-white font-medium shadow-lg shadow-primary-500/20 hover:shadow-primary-500/40 transition-all duration-300"
          >
            <span>Discuss a Potential Acquisition</span>
            <ArrowRight size={18} className="ml-2" />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default SpecialtyStructures;
